import React from "react";
import { Box, Button, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { toolbarButtonStyle } from "../../styles/buttonStyles";

interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  pageSize,
  total,
  onPageChange,
}) => {
  const totalPages = Math.ceil(total / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  if (totalPages <= 1) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: "space-between",
        gap: { xs: 2, sm: 0 },
        mt: { xs: 3, sm: 4 },
        pt: { xs: 2, sm: 3 },
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontSize: { xs: "0.875rem", sm: "0.875rem" },
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        Showing {startItem}-{endItem} of {total}
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 1 },
          justifyContent: { xs: "center", sm: "flex-end" },
        }}
      >
        <Button
          size="small"
          variant="text"
          startIcon={<ChevronLeftIcon />}
          disableRipple
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
          sx={{
            ...toolbarButtonStyle,
            minHeight: { xs: "44px", sm: "32px" },
            fontSize: { xs: "0.875rem", sm: "0.875rem" },
            px: { xs: 2, sm: 1 },
            border: "none",
            boxShadow: "none",
          }}
        >
          Previous
        </Button>

        <Typography
          variant="body2"
          sx={{
            display: "flex",
            alignItems: "center",
            px: { xs: 1, sm: 2 },
            color: "text.secondary",
            fontSize: { xs: "0.875rem", sm: "0.875rem" },
          }}
        >
          Page {page} of {totalPages}
        </Typography>

        <Button
          size="small"
          variant="text"
          endIcon={<ChevronRightIcon />}
          disableRipple
          disabled={page === totalPages}
          onClick={() => onPageChange(page + 1)}
          sx={{
            ...toolbarButtonStyle,
            minHeight: { xs: "44px", sm: "32px" },
            fontSize: { xs: "0.875rem", sm: "0.875rem" },
            px: { xs: 2, sm: 1 },
            border: "none",
            boxShadow: "none",
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};
